/**
 * Sticky Checkout Button Component for Mobile Cart
 * 
 * Toggles 'is-sticky' class on .cart__ctas based on:
 * - Original button position (.cart__summary-item) visibility in viewport
 * - Keyboard state
 * - Mobile breakpoint
 * 
 * Logic: When original position is visible, hide sticky to avoid duplicate buttons
 */

if (!customElements.get('cart-sticky-checkout')) {
  class CartStickyCheckout extends HTMLElement {
    constructor() {
      super();
      this.cartCtas = null;
      this.cartSummaryTotals = null;
      this.observer = null;
      this.keyboardOpen = false;
      this.lastViewportHeight = window.visualViewport?.height || window.innerHeight;
      this.lastViewportWidth = window.visualViewport?.width || window.innerWidth;
      this.isMobile = false;
    }

    connectedCallback() {
      // Check if mobile
      this.checkMobile();
      if (!this.isMobile) return;

      // Find elements - single source of truth
      if (!this.findTargetElements()) {
        console.warn('Sticky checkout: Required elements not found');
        return;
      }

      // Set up Intersection Observer
      this.setupIntersectionObserver();
      
      // Set up keyboard detection
      this.setupKeyboardDetection();
      
      // Set up resize listener
      this.setupResizeListener();
      
      // Listen for cart updates
      this.setupCartUpdateListener();
      
      // Do initial check on page load
      this.checkSummaryVisibility();
    }
    
    findTargetElements() {
      // Single source of truth for finding target elements
      this.cartCtas = this.querySelector('.cart-mobile-footer .cart__ctas');
      // Observe the original button position (.cart__summary-item or .tax-note)
      this.cartSummaryTotals = this.querySelector('.cart-mobile-footer .cart__summary-item')
        || this.querySelector('.cart-mobile-footer .tax-note')
        || this.cartCtas;
      
      return this.cartCtas && this.cartSummaryTotals;
    }
    
    setupCartUpdateListener() {
      // Listen for cart update events and reinitialize
      this.handleCartUpdate = () => {
        if (!this.isMobile) return;
        
        // Get fresh element references from single source
        if (!this.findTargetElements()) return;
        
        // Reconnect observer with new elements
        if (this.observer) {
          this.observer.disconnect();
          this.setupIntersectionObserver();
        }
        
        // Check visibility immediately
        this.checkSummaryVisibility();
      };
      
      const cartItemsComponent = this.querySelector('cart-items-component');
      if (cartItemsComponent) {
        cartItemsComponent.addEventListener('cart:update', this.handleCartUpdate);
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (window.visualViewport && this.handleViewportResize) {
        window.visualViewport.removeEventListener('resize', this.handleViewportResize);
      }
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
    }

    checkMobile() {
      this.isMobile = window.innerWidth <= 990;
    }

    setupResizeListener() {
      this.handleResize = () => {
        const wasMobile = this.isMobile;
        this.checkMobile();
        
        // If switching between mobile/desktop
        if (wasMobile !== this.isMobile) {
          if (!this.isMobile) {
            // Remove sticky class on desktop
            this.removeSticky();
          } else {
            // Switched back to mobile - reset keyboard state
            this.keyboardOpen = false;
            this.checkSummaryVisibility();
          }
        } else if (this.isMobile) {
          // Still mobile but dimensions changed (e.g., orientation change)
          // Reset keyboard state and recalculate sticky state
          this.keyboardOpen = false;
          this.checkSummaryVisibility();
        }
      };
      
      window.addEventListener('resize', this.handleResize);
    }

    setupIntersectionObserver() {
      // Monitor when original button position enters viewport
      const options = {
        root: null,
        rootMargin: '0px 0px -50px 0px', // Trigger 50px before bottom of viewport
        threshold: 0.1 // 10% visible
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!this.isMobile) return;
          
          if (entry.isIntersecting) {
            // Original button position is in viewport - remove sticky
            this.removeSticky();
          } else {
            // Original button position not in viewport - add sticky
            if (!this.keyboardOpen) {
              this.addSticky();
            }
          }
        });
      }, options);

      if (this.cartSummaryTotals) {
        this.observer.observe(this.cartSummaryTotals);
      }
    }

    setupKeyboardDetection() {
      // Hide when keyboard is open
      if (window.visualViewport) {
        this.handleViewportResize = () => {
          if (!window.visualViewport) return;
          const currentHeight = window.visualViewport.height;
          const currentWidth = window.visualViewport.width;
          const heightDiff = this.lastViewportHeight - currentHeight;
          const widthDiff = Math.abs(this.lastViewportWidth - currentWidth);
          
          // If both width and height changed significantly, it's likely orientation change, not keyboard
          const isOrientationChange = widthDiff > 100;
          
          // Keyboard is likely open if viewport height decreased significantly (> 100px)
          // BUT only if width didn't change much (not an orientation change)
          if (heightDiff > 100 && !isOrientationChange) {
            this.keyboardOpen = true;
            this.removeSticky();
          } else if (heightDiff < -50 && !isOrientationChange) {
            // Keyboard closed
            this.keyboardOpen = false;
            // Re-check if should be sticky
            this.checkSummaryVisibility();
          } else if (isOrientationChange) {
            // Orientation change - reset keyboard state
            this.keyboardOpen = false;
          }
          
          this.lastViewportHeight = currentHeight;
          this.lastViewportWidth = currentWidth;
        };
        
        window.visualViewport.addEventListener('resize', this.handleViewportResize);
      } else {
        // Fallback: Listen for focus on input fields
        document.addEventListener('focusin', (e) => {
          const target = /** @type {HTMLElement} */ (e.target);
          if (target && target.matches && target.matches('input, textarea, select')) {
            this.keyboardOpen = true;
            this.removeSticky();
          }
        });
        
        document.addEventListener('focusout', () => {
          // Reset keyboard state when leaving form fields
          this.keyboardOpen = false;
          this.checkSummaryVisibility();
        });
      }
    }

    checkSummaryVisibility() {
      // Check if original button position is visible in viewport
      if (!this.cartSummaryTotals || !this.isMobile) return;
      
      const rect = this.cartSummaryTotals.getBoundingClientRect();
      const isVisible = (
        rect.top < window.innerHeight &&
        rect.bottom > 0
      );
      
      if (isVisible) {
        this.removeSticky();
      } else if (!this.keyboardOpen) {
        this.addSticky();
      }
    }

    addSticky() {
      if (this.cartCtas && this.isMobile) {
        this.cartCtas.classList.add('is-sticky');
      }
    }

    removeSticky() {
      if (this.cartCtas) {
        this.cartCtas.classList.remove('is-sticky');
      }
    }
  }

  customElements.define('cart-sticky-checkout', CartStickyCheckout);
}
